import {
    Button,
    Col,
    Descriptions,
    Divider,
    Drawer,
    Form,
    FormInstance,
    Row,
} from 'antd';
import { FormProps } from 'antd/es/form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DescriptionItem } from '@/v1/domain/models';
import { Icon, IconImg } from '@/v1/main/views/Icon';
import {
    ModalOption,
    ModalType,
    useDevice,
    useError,
    useModal,
} from '@/v1/main/views/Provider';

export interface MainFormProps<T> extends FormProps {
    open: boolean;
    title: string;
    descriptions?: DescriptionItem[];
    children?: React.ReactNode;
    footer?: React.ReactNode;
    onClose: () => void;
    onSubmit?: (values: T) => void;
    onFail?: (err: unknown, form: FormInstance) => void;
    submitText?: string;
}

export const MainForm = <T,>({
    open,
    title,
    descriptions,
    initialValues = {},
    children,
    footer,
    onClose,
    onSubmit,
    onFail,
    submitText,
    ...reset
}: MainFormProps<T>) => {
    const { t } = useTranslation(['interface', 'title', 'field']);
    const { isMobileMode } = useDevice();
    const { setModalOption, destroy } = useModal();
    const { onRequestFail } = useError();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(false);

    const onFinish = async () => {
        setLoading(true);
        try {
            const values = await form.validateFields();

            const data = form.getFieldsValue();
            const isEqual = Object.keys(data).reduce(
                (isEqual, key) => isEqual && data[key] == initialValues[key],
                true
            );
            if (!isEqual) {
                if (onSubmit) await onSubmit({ ...initialValues, ...values });
            }
            onClose();
            form.resetFields();
        } catch (err: unknown) {
            await onFinishFail(err);
        }
        setLoading(false);
    };

    const onFinishFail = async (err: unknown) => {
        console.trace(err);

        const isFormValid = form
            .getFieldsError()
            .every((fieldErr) => fieldErr.errors.length === 0);
        if (!isFormValid) {
            return;
        }
        try {
            await onRequestFail(err, form);
        } catch (err) {
            if (onFail) onFail(err, form);
        }
    };

    const onCancel = () => {
        const data = form.getFieldsValue();
        const isEqual = Object.keys(data).reduce(
            (isEqual, key) => isEqual && data[key] == initialValues[key],
            true
        );
        if (isEqual) {
            onClose();
            return;
        }
        const onOk = () => {
            onClose();
            form.resetFields();
            destroy();
        };
        const onCancel = () => {
            destroy();
        };
        const opt: ModalOption = {
            type: ModalType.WARNING,
            title: t('notification.leave_form.title', { ns: 'interface' }),
            content: t('notification.leave_form.tip', { ns: 'interface' }),
            onOk,
            onCancel,
        };
        setModalOption(opt);
    };

    const closeIcon = isMobileMode ? (
        <Icon value={IconImg.MdArrowBackIos} />
    ) : undefined;
    const defaultFooter = (
        <Row
            gutter={8}
            align="middle"
            justify={isMobileMode ? 'space-around' : 'end'}
        >
            <Col>
                <Button
                    type="text"
                    onClick={onCancel}
                    style={{ width: '100%' }}
                >
                    {t('action.cancel', { ns: 'interface' })}
                </Button>
            </Col>
            <Col span={isMobileMode ? 16 : undefined}>
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    onClick={onFinish}
                    style={{ width: '100%' }}
                >
                    {submitText ?? t('action.submit', { ns: 'interface' })}
                </Button>
            </Col>
        </Row>
    );
    return (
        <Drawer
            title={title}
            closeIcon={closeIcon}
            placement="right"
            open={open}
            onClose={onCancel}
            footer={footer ?? defaultFooter}
        >
            <Form
                layout="vertical"
                form={form}
                scrollToFirstError={true}
                validateTrigger="onSubmit"
                initialValues={initialValues}
                {...reset}
            >
                {descriptions && (
                    <>
                        <Descriptions column={1} size="small">
                            {descriptions.map((decs, index) => (
                                <Descriptions.Item
                                    key={index}
                                    label={decs.label}
                                >
                                    {decs.value}
                                </Descriptions.Item>
                            ))}
                        </Descriptions>
                        <Divider />
                    </>
                )}
                {children}
            </Form>
        </Drawer>
    );
};
